import React, { useState, useEffect } from "react";
import { Redirect } from "react-router-dom";

import { useTunnelData } from "../TunnelContext";
import Api from "../baseApi";
import LoadingScreen from "../components/LoadingScreen";
import OAButton from "../components/OAButton";
import SubscriptionSummary from "../components/SubscriptionSummary";
import { ChevronLeft } from "lucide-react";

import Breadcrumbs from "../components/Breadcrumbs";
import { Link } from "react-router-dom/cjs/react-router-dom";
import { useTranslation } from "react-i18next";
import OAInput from "../components/input/OAInput";
import Footer from "../components/Footer";
import { Validators } from "../lib/validators";
import pushEvent from "../pushEvent";
import { useConfig } from "../ConfigProvider";

export default function Sepa() {
  const [step, setStep] = useState("FORM");
  const { tunnel, setTunnel } = useTunnelData();
  const [errors, setErrors] = useState({});
  const config = useConfig();

  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);

    pushEvent({
      event: "funnel_step_4",
      step_name: "Abonnement",
      funnel_club: tunnel.club.name,
      funnel_club_status: "Ouvert",
      funnel_abo: tunnel.offer.offer.name,
    });
  }, [tunnel]);

  if (!tunnel.sales) return <Redirect push to={"/"} />;

  if (!tunnel.offer.d.needSepa) {
    return <Redirect push to="/card" />;
  }
  const setError = (field, error) => {
    if (error) setErrors((prev) => ({ ...prev, [field]: error }));
    else
      setErrors((prev) => {
        let err = prev;
        delete err[field];
        return { ...err };
      });
  };

  const handleChange = (ev) => {
    const { name, value } = ev.target;
    setTunnel({ ...tunnel, payment: { ...tunnel.payment, [name]: value } });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTunnel({ ...tunnel, payment: { ...tunnel.payment, [name]: value } });

    let validation;

    switch (name) {
      case "iban":
        validation = Validators.validateIBAN(value);
        if (!validation.ok) setError(name, validation.error);
        else setError(name, null);
        break;
      case "bic":
        validation = Validators.validateBIC(value);
        if (!validation.ok) setError(name, validation.error);
        else setError(name, null);
        break;
      case "honeypot":
        if (value) setError(name, "catched");
        break;
      default:
        validation = Validators.requiredField(value);
        if (!validation.ok) setError(name, validation.error);
        else setError(name, null);
        break;
    }
  };

  const validateAllFields = () => {
    const fieldsToValidate = ["holder", "iban", "honeypot"];
    if (config.code !== "onair.es") {
      fieldsToValidate.push("bank");
      fieldsToValidate.push("bic");
    }

    fieldsToValidate.forEach((field) => {
      const value = tunnel.payment?.[field] || "";
      handleInputChange({
        target: {
          name: field,
          value,
        },
      });
    });
    if (Object.keys(errors).map((key) => errors[key] !== null).length > 0) {
      return false;
    } else {
      return true;
    }
  };

  const onSubmit = () => {
    if (!validateAllFields()) {
      return;
    }

    setStep("CREATE");
    const { contactResa, payment } = tunnel;

    const mandate = {
      clubId: contactResa.clubId,
      contactId: contactResa["@id"],
      contactFamilyName: contactResa.familyName,
      contactGivenName: contactResa.givenName,
      contactNumber: contactResa.number,
      contactAddress: {
        addressCountry: contactResa.address.addressCountry,
        addressLocality: contactResa.address.addressLocality,
        postalCode: contactResa.address.postalCode,
        streetAddress: contactResa.address.streetAddress,
      },
      holder: payment.holder,
      iban: payment.iban,
    };

    if (config.code !== "onair.es" && config.code !== "onair.test") {
      mandate.bic = payment.bic;
      mandate.bank = payment.bank;
    }

    if (config.code === "onair.es" || config.code === "onair.test") {
      mandate.bank = " ";
    }

    const [query] = Api.POST({
      path: "/api/abo/resamania2/mandates",
      body: {
        tunnelUid: tunnel.uid,
        msdsId: tunnel.club.msdsId,
        mandate,
      },
    });

    query
      .then((mandate) => {
        setTunnel({ ...tunnel, mandate });
        setStep("CONTRACT");
      })
      .catch((e) => {
        console.error(e);
        setStep("FORM");
        //errorSet(e.userMsg || "Une erreur est survenue");
      });
  };

  const isValid = () => {
    const p = tunnel.payment;
    if (!p) return false;

    if (!p.holder || !p.iban) return false;

    if (config.code !== "onair.es" && config.code !== "onair.test" && !p.bank && !p.bic) return false;

    return true;
  };

  if (step === "CONTRACT") {
    pushEvent({
      event: "funnel_step_4_sepa",
      step_name: "Abonnement",
      funnel_club: tunnel.club.name,
      funnel_club_status: "Ouvert",
      funnel_abo: tunnel.offer.offer.name,
    });
    return <Redirect push to={"/contract"} />;
  }
  //WIP remove
  if (step === "CARD") return <Redirect push to={"/card"} />;

  if (step === "CREATE") return <LoadingScreen LoadingText={t("loading_screen.loading_contract")} />;

  //w-full sm:w-4/5 lg:w-2/3 relative sm:my-2 xl:w-[27%] xl:absolute xl:top-1/4 xl:right-[1%] 2xl:-right-[-1%]
  return (
    <>
      <div className="bg-oaBackgroundColor min-h-screen w-full">
        <Link
          to="/user"
          className="absolute left-4 top-5 md:left-20 flex flex-row hover:text-oaRed transition-all duration-300"
        >
          <ChevronLeft className="mr-2" />
          {t("previous_step")}
        </Link>
        <section className="w-full h-full px-5 py-4 md:px-20 md:py-10">
          <div className="animate-fade-in-left">
            <Breadcrumbs />
          </div>
          <h2 className="text-base text-center font-bold pt-6 animate-fade-in-left mx-auto lg:max-w-[40%] lg:text-4xl">
            {t("sepa.title").toUpperCase()}
          </h2>
          <p className="text-base hidden lg:block lg:text-2xl text-center font-light py-3 animate-fade-in-left mx-auto md:max-w-[326px] lg:max-w-[40%] ">
            {t("sepa.sub_title")}
          </p>
          <div className="flex m-auto w-full justify-center max-w-[672px]">
            <form onSubmit={onSubmit} className="flex justify-center flex-col">
              <section className="flex flex-col p-4 rounded-b-[8px] animate-fade-in-down gap-6">
                <fieldset className="flex flex-col ">
                  <OAInput
                    onChange={handleInputChange}
                    onBlur={handleInputChange}
                    name={"holder"}
                    type="text"
                    placeholder={t("sepa.placeholders.account_of")}
                    value={tunnel.payment?.holder || ""}
                    error={errors.holder}
                    required
                  />
                </fieldset>
                <fieldset className="flex flex-col ">
                  <OAInput
                    onChange={handleInputChange}
                    onBlur={handleInputChange}
                    name={"iban"}
                    type="text"
                    placeholder={t("sepa.placeholders.iban").toUpperCase()}
                    value={tunnel.payment?.iban || ""}
                    error={errors.iban}
                  />
                </fieldset>

                {config.code !== "onair.es" && (
                    <>
                      <fieldset className="w-full flex flex-col">
                        <OAInput
                          onChange={handleInputChange}
                          onBlur={handleInputChange}
                          name={"bic"}
                          type="text"
                          placeholder={t("sepa.placeholders.bic").toUpperCase()}
                          value={tunnel.payment?.bic || ""}
                          error={errors.bic}
                        />
                      </fieldset>
                      <fieldset className="w-full flex flex-col">
                        <OAInput
                          onChange={handleInputChange}
                          onBlur={handleInputChange}
                          name={"bank"}
                          type="text"
                          placeholder={t("sepa.placeholders.bank_name")}
                          value={tunnel.payment?.bank || ""}
                          error={errors.bank}
                        />
                      </fieldset>
                    </>
                  )}
              </section>
              <p className="text-xs font-medium text-oaBackgroundText px-4">{t("sepa.info")}</p>
              <OAInput name={"honeypot"} onChange={handleInputChange} type="hidden" />
              <section className="w-full flex flex-col gap-6 lg:flex-row-reverse justify-between items-center mt-8">
                {step !== "LOADING" && (
                  <OAButton
                    onClick={onSubmit}
                    disabled={!isValid()}
                    className={"w-2/3 lg:w-full h-9 text-base font-extrabold animate-fade-in-right"}
                  >
                    <p className="lg:hidden">{t("sepa.submit.phone").toUpperCase()}</p>
                    <p className="hidden lg:inline">{t("sepa.submit.pc").toUpperCase()}</p>
                  </OAButton>
                )}
              </section>
            </form>
          </div>
          {!tunnel.stripe && (
            <SubscriptionSummary
              club={tunnel.club}
              offer={tunnel.offer}
              options={tunnel.options}
              regulars={tunnel.regulars}
            />
          )}
        </section>
      </div>
      <footer>
        <Footer></Footer>
      </footer>
    </>
  );
}
